import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation, useQuery } from '@tanstack/react-query';
import { Clock, Edit2, FolderKanban, MessageSquare, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { agentClient } from '../../lib/connect/clients';
import { formatRelativeTime } from '../../utils/time';
import { chatHref } from './chatHref';
import { SharedWithMeList } from './SharedWithMeList';
// Strip the legacy `shanks-` chatId prefix before any RPC. The prefix is a
// wire-level affordance for routing in the frontend (so /assistant/<id>
// still works after the cut from express); the pangaea collab.conversations
// row and the shanks AgentService both key off the bare uuid.
function bareChatId(chatId) {
    return chatId.startsWith('shanks-') ? chatId.slice(7) : chatId;
}
/**
 * Fetches chat history via the AgentService.ListHistory Connect RPC.
 * Replaces the legacy express /api/chat REST endpoint.
 *
 * The express handler enriched each chat with `lastMessage` preview and
 * `projectName` — those fields are not on AgentService.Conversation today
 * and land as a separate proto extension. The sidebar renders title +
 * timestamp without the preview line until then.
 */ async function fetchChats() {
    const resp = await agentClient.listHistory({
        userId: '',
        limit: 10000,
        offset: 0
    });
    const chats = resp.conversations.map((c)=>{
        const createdAt = c.createdAt?.toDate().toISOString() ?? new Date(0).toISOString();
        const updatedAt = c.updatedAt?.toDate().toISOString() ?? createdAt;
        return {
            chatId: `shanks-${c.id}`,
            title: c.title,
            createdAt,
            updatedAt,
            projectId: c.projectId || undefined,
            projectName: undefined,
            lastMessage: undefined
        };
    });
    return {
        chats,
        pagination: {
            total: chats.length,
            page: 1,
            limit: chats.length,
            totalPages: 1
        }
    };
}
/** Delete a chat via AgentService.DeleteConversation. */ async function deleteChat(chatId) {
    await agentClient.deleteConversation({
        id: bareChatId(chatId)
    });
}
/** Update a chat's title via AgentService.UpdateConversation. */ async function updateChatTitle(chatId, title) {
    await agentClient.updateConversation({
        id: bareChatId(chatId),
        title
    });
}
/**
 * Displays a list of chat conversations with timestamps and previews
 * @param props Component props
 * @returns Chat history component
 */ export function ChatHistory({ className, sortBy = 'recent', searchQuery = '' } = {}) {
    const [activeTab, setActiveTab] = useState('my-chats');
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [editingChat, setEditingChat] = useState(null);
    const [editTitle, setEditTitle] = useState('');
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: [
            'chats'
        ],
        queryFn: fetchChats,
        staleTime: 30000
    });
    const deleteMutation = useMutation({
        mutationFn: deleteChat,
        onSuccess: ()=>{
            setDeleteConfirm(null);
            refetch();
        },
        onError: (err)=>{
            console.error('Failed to delete chat:', err);
        }
    });
    const updateMutation = useMutation({
        mutationFn: ({ chatId, title })=>updateChatTitle(chatId, title),
        onSuccess: ()=>{
            setEditingChat(null);
            refetch();
        },
        onError: (err)=>{
            console.error('Failed to update chat:', err);
        }
    });
    const handleDelete = (chatId)=>{
        deleteMutation.mutate(chatId);
    };
    const handleEditStart = (chatId, currentTitle)=>{
        setEditingChat(chatId);
        setEditTitle(currentTitle);
    };
    const handleEditSave = (chatId)=>{
        if (!editTitle.trim()) {
            return;
        }
        updateMutation.mutate({
            chatId,
            title: editTitle.trim()
        });
    };
    const sortedChats = useMemo(()=>{
        const allChats = data?.chats || [];
        // Filter by search query
        const filtered = allChats.filter((chat)=>chat.title.toLowerCase().includes(searchQuery.toLowerCase()));
        // Sort
        switch(sortBy){
            case 'oldest':
                return filtered.sort((a, b)=>new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            case 'name':
                return filtered.sort((a, b)=>a.title.localeCompare(b.title));
            default:
                return filtered.sort((a, b)=>new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        }
    }, [
        data?.chats,
        searchQuery,
        sortBy
    ]);
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center h-64",
            children: /*#__PURE__*/ _jsx("div", {
                className: "animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-500",
                role: "status",
                "aria-label": "Loading chat history"
            })
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx("div", {
            className: "text-red-600 dark:text-red-400 p-4 border border-red-200 dark:border-red-900/30 rounded-lg bg-red-50 dark:bg-red-900/10",
            children: "Failed to load chat history. Please try again."
        });
    }
    const allChats = data?.chats || [];
    const handleChatSelect = (chatId)=>{
        // Navigate to chat - implementation depends on router setup
        window.location.href = `/assistant/${chatId}`;
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: className ?? 'space-y-2',
        "data-testid": "chat-history",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex border-b border-border",
                children: [
                    /*#__PURE__*/ _jsx("button", {
                        onClick: ()=>setActiveTab('my-chats'),
                        className: `px-4 py-2 text-sm font-medium transition-colors ${activeTab === 'my-chats' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`,
                        children: "My Chats"
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        onClick: ()=>setActiveTab('shared-with-me'),
                        className: `px-4 py-2 text-sm font-medium transition-colors ${activeTab === 'shared-with-me' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`,
                        children: "Shared with me"
                    })
                ]
            }),
            activeTab === 'my-chats' ? isLoading ? /*#__PURE__*/ _jsx("div", {
                className: "flex items-center justify-center h-64",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-500",
                    role: "status",
                    "aria-label": "Loading chat history"
                })
            }) : error ? /*#__PURE__*/ _jsx("div", {
                className: "text-red-600 dark:text-red-400 p-4 border border-red-200 dark:border-red-900/30 rounded-lg bg-red-50 dark:bg-red-900/10",
                children: "Failed to load chat history. Please try again."
            }) : sortedChats.length === 0 && allChats.length === 0 ? /*#__PURE__*/ _jsx(EmptyState, {}) : sortedChats.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                className: "text-center text-gray-600 dark:text-gray-400 py-8 border border-gray-200 dark:border-gray-700 rounded-lg",
                children: [
                    /*#__PURE__*/ _jsx(MessageSquare, {
                        className: "w-8 h-8 mx-auto mb-2 opacity-50"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm",
                        children: "No conversations match your search"
                    })
                ]
            }) : sortedChats.map((chat)=>/*#__PURE__*/ _jsx(ChatListItem, {
                    chat: chat,
                    onDelete: ()=>setDeleteConfirm(chat.chatId),
                    isDeleting: deleteConfirm === chat.chatId,
                    onConfirmDelete: ()=>handleDelete(chat.chatId),
                    onCancelDelete: ()=>setDeleteConfirm(null),
                    onEditStart: ()=>handleEditStart(chat.chatId, chat.title),
                    isEditing: editingChat === chat.chatId,
                    editTitle: editTitle,
                    onEditTitleChange: setEditTitle,
                    onEditSave: ()=>handleEditSave(chat.chatId),
                    onEditCancel: ()=>setEditingChat(null)
                }, chat.chatId)) : /*#__PURE__*/ _jsx(SharedWithMeList, {
                onChatSelect: handleChatSelect
            })
        ]
    });
}
/**
 * Empty state component shown when no chats exist
 */ function EmptyState() {
    return /*#__PURE__*/ _jsxs("div", {
        className: "text-center text-gray-600 dark:text-gray-400 py-12 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg",
        children: [
            /*#__PURE__*/ _jsx(MessageSquare, {
                className: "w-12 h-12 mx-auto mb-4 opacity-50"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-lg font-medium text-gray-900 dark:text-white",
                children: "No conversations yet"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm mt-2",
                children: "Start a new chat to begin"
            }),
            /*#__PURE__*/ _jsx("a", {
                href: "/assistant",
                className: "inline-block mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded-lg transition-colors text-sm font-medium",
                "data-testid": "new-chat-button",
                children: "Start a New Chat"
            })
        ]
    });
}
function ChatListItem({ chat, onDelete, isDeleting, onConfirmDelete, onCancelDelete, onEditStart, isEditing, editTitle, onEditTitleChange, onEditSave, onEditCancel }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "group relative",
        children: [
            isDeleting && /*#__PURE__*/ _jsxs("div", {
                className: "absolute inset-0 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-900/50 flex items-center justify-center gap-2 z-10",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm font-medium text-gray-900 dark:text-white",
                        children: "Delete this chat?"
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        onClick: onConfirmDelete,
                        className: "px-2 py-1 text-xs bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white rounded transition-colors",
                        children: "Delete"
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        onClick: onCancelDelete,
                        className: "px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors",
                        children: "Cancel"
                    })
                ]
            }),
            isEditing && /*#__PURE__*/ _jsxs("div", {
                className: "absolute inset-0 bg-gray-50 dark:bg-gray-700 rounded-lg border border-indigo-200 dark:border-indigo-900/50 flex items-center gap-2 z-10 px-4",
                children: [
                    /*#__PURE__*/ _jsx("input", {
                        type: "text",
                        value: editTitle,
                        onChange: (e)=>onEditTitleChange(e.target.value),
                        className: "flex-1 px-2 py-1 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-600 dark:focus:ring-indigo-500",
                        onKeyDown: (e)=>{
                            if (e.key === 'Enter') onEditSave();
                            if (e.key === 'Escape') onEditCancel();
                        },
                        // biome-ignore lint/a11y/noAutofocus: Input needs focus for immediate editing
                        autoFocus: true
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        onClick: onEditSave,
                        className: "px-2 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded transition-colors",
                        children: "Save"
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        onClick: onEditCancel,
                        className: "px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors",
                        children: "Cancel"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Link, {
                to: chatHref(chat),
                className: "block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-start justify-between gap-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex-1 min-w-0",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "font-medium truncate text-gray-900 dark:text-white",
                                            children: chat.title
                                        }),
                                        chat.projectId && /*#__PURE__*/ _jsxs("span", {
                                            className: "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
                                            "data-testid": "project-chat-badge",
                                            children: [
                                                /*#__PURE__*/ _jsx(FolderKanban, {
                                                    className: "w-3 h-3"
                                                }),
                                                chat.projectName || 'Project'
                                            ]
                                        })
                                    ]
                                }),
                                chat.lastMessage && /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-gray-600 dark:text-gray-400 truncate mt-1",
                                    children: chat.lastMessage.preview
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 group-hover:hidden",
                            children: [
                                /*#__PURE__*/ _jsx(Clock, {
                                    className: "w-3 h-3"
                                }),
                                /*#__PURE__*/ _jsx("time", {
                                    dateTime: chat.updatedAt,
                                    children: formatRelativeTime(chat.updatedAt)
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("button", {
                            onClick: (e)=>{
                                e.preventDefault();
                                onEditStart();
                            },
                            className: "hidden group-hover:flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-xs text-gray-600 dark:text-gray-400",
                            title: "Edit chat name",
                            "aria-label": "Edit chat name",
                            children: [
                                /*#__PURE__*/ _jsx(Edit2, {
                                    className: "w-3 h-3"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    children: "Edit"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("button", {
                            onClick: (e)=>{
                                e.preventDefault();
                                onDelete();
                            },
                            className: "hidden group-hover:flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors text-xs text-gray-600 dark:text-gray-400",
                            title: "Delete chat",
                            "aria-label": "Delete chat",
                            children: [
                                /*#__PURE__*/ _jsx(Trash2, {
                                    className: "w-3 h-3"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    children: "Delete"
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
